import { RefObject, useEffect, useRef } from 'react';

export type UseRefArrayProps<T> = {
    size: number;
    initialValue: T;
};

function createRefArray<T>({ size, initialValue }: UseRefArrayProps<T>) {
    // In case `initialValue` is an object type, clone it so every element has its own reference
    return size > 0 ? Array.from(Array(size)).map(() => structuredClone(initialValue)) : [];
}

export function useRefArray<T>({ size, initialValue }: UseRefArrayProps<T>): RefObject<T[]> {
    const ref = useRef(createRefArray({ size, initialValue }));

    useEffect(() => {
        const currentSize = ref.current.length;
        // Will be empty if `size - currentSize` is negative
        const newItems = createRefArray({ size: size - currentSize, initialValue });

        // If new size is greater, this will copy existing items and add new ones up to new size
        // If new size is smaller, this will cut existing items down to new size, and add 0 new items
        ref.current = [...ref.current.slice(0, size), ...newItems];
    }, [size, initialValue]);

    return ref;
}
